import {defineStore} from 'pinia'
import {nextTick} from 'vue'
import axios from 'axios'
import {useProductListStore} from './index'
import {i18n} from '@/plugins/i18n' // import i18n instance
import {formatLocalizedDecimal, getDecimalLocale, parseNumberToLocalizedPrice} from '@/utils/format'

function isNum(v) {
    return typeof v === 'number' && Number.isFinite(v)
}

const IGNORED_URL_KEYS = new Set([
    // Internal / app keys
    'classifications', 'itemNos', 'brand', 'productFamily',
    'division', 'locale', 'site', 'pageSize', 'pageNumber',
    // Google / Ads
    'gclid', 'gclsrc', 'dclid', 'gbraid', 'wbraid', 'gad_source',
    // Meta / Facebook
    'fbclid', 'fb_action_ids', 'fb_action_types', 'fb_source', 'fb_ref',
    // Microsoft / Bing
    'msclkid',
    // Google Search
    'srsltid',
    // AB Tasty
    'abtasty_editor',
    // Analytics / tracking
    'mc_cid', 'mc_eid',                   // Mailchimp
    '_ga', '_gl', '_gac',                  // Google Analytics cross-domain
    'yclid',                               // Yandex
    'ttclid',                              // TikTok
    'twclid',                              // Twitter / X
    'li_fat_id',                           // LinkedIn
    'igshid',                              // Instagram
    'sc_cid',                              // Snapchat
    'epik',                                // Pinterest
    'hsa_cam', 'hsa_grp', 'hsa_mt', 'hsa_src', 'hsa_ad', 'hsa_acc',
    'hsa_net', 'hsa_ver', 'hsa_la', 'hsa_ol', 'hsa_kw', 'hsa_tgt', // HubSpot
    'ref', 'referrer', 'source', 'medium', // Generic attribution
    'campaign', 'content', 'term',         // Generic campaign
    'redirect', 'returnUrl', 'return_url', // Redirect params
    'cHash', 'type', 'id', 'L', 'tx_news_pi1[news]', // TYPO3 system params
])

const IGNORED_URL_PREFIXES = ['utm_', 'pk_', 'mtm_', 'piwik_', 'matomo_']

function shouldIgnoreParam(key) {
    if (IGNORED_URL_KEYS.has(key)) return true
    return IGNORED_URL_PREFIXES.some(prefix => key.startsWith(prefix))
}

let _urlSyncTimer = null
let _urlSyncActive = false

/**
 * Parse a numeric range from a string.
 * Supports: "min:10,max:50", "min=10,max=50", "10..50" | "10-50" | "10;50" | "10,50"
 */
function parseRangeFromString(str) {
    if (typeof str !== 'string') return null

    const s = str.trim()

    if (!s) return null

    // min:max style "min:10,max:50"
    const named = /min\s*[:=]\s*([+-]?\d*\.?\d+)\s*,\s*max\s*[:=]\s*([+-]?\d*\.?\d+)/i.exec(s)

    if (named) return [Number(named[1]), Number(named[2])]

    // separators: ".." | "-" | ";" | ","
    const sep = s.includes('..') ? '..' : (s.includes('-') ? '-' : (s.includes(';') ? ';' : ','))
    const parts = s.split(sep).map(x => x.trim())

    if (parts.length !== 2 || !parts[0] || !parts[1]) return null

    if (!/^[+-]?\d*\.?\d+$/.test(parts[0]) || !/^[+-]?\d*\.?\d+$/.test(parts[1])) return null

    return [Number(parts[0]), Number(parts[1])]
}

/**
 * A Pinia store managing available filters, selected values, and
 * fetching them from the backend.
 */
export const useFilterStore = defineStore('filterStore', {
    state: () => ({
        apiEndpoint: window.app.product_list.api_endpoint, website: window.app.product_list.api_site, // locale in format "en_US" etc.
        locale: window.app.product_list.itemrepoLocale
            || window.app.product_list.siteLanguageHrefLang.replace('-', '_'),
        baseTpgCodes: [], // does not change
        skus: [], // product SKUs in current list
        itemNos: [], // product item numbers
        filterCategories: [], // array of { key, description }
        categoryDescriptions: {}, // Cache of key → last known description (survives category disappearing).
        filterOptions: {}, // map key → metadata payload
        selected: {}, // map key → array|boolean|[min,max]
        availability: {
            // key -> availability description
            // OPTION/TPG_OPTION: { type: 'OPTION', values: Set<string> }
            // RANGE: { type: 'PRICE'|'NUMBER', min: number, max: number }
            // BOOLEAN: { type: 'BOOLEAN', possible: true }  // simple flag
        }, lastInteractedKey: null, allOptionValues: {}, // key -> immutable full list of options, used for rendering
        allRangeBounds: {}, // key -> { min, max } from the first fetch
        everFetchedKeys: new Set(), // stores for which categories options have been loaded already
        userTouchedRange: {}, // Tracks if user has actively changed the range for a key.
        loadingCategories: false, // true while fetching category list
        loadingOptions: {}, // map key → loading boolean
        loadingApply: false, loaded: false, error: null, // hold last error
        silenceSelectedMutations: 0, _initDone: false, _fetchCatsPromise: null, _isHydrating: true, // turns false only after initial options fetched
        _abortCtrl: null, // internal: abort running /v2/website/filter request when new one starts
        _skipNextAvailabilityFor: new Set(), // temp guard to skip one redundant availability refresh (e.g. on first load)
        _lastBaseTpgCodesKey: '', _baseTpgSource: '', // '', 'classifications', 'items'
        _isResetting: false,
        _hasAppliedOnce: false,
        _firstLoadOptionsKey: null, // which key is allowed to fetch on first load
        _prefilterOnlyAvailableAdded: false, // true only if we added onlyAvailable automatically
        _prefilterOnlyAvailableFallbackDone: false, // prevents retry loops
    }),

    getters: {
        /** Get raw metadata (type/min/max/optionValues) for a filter key. */
        metaFor: (state) => (key) => state.filterOptions[key] || null,

        /** Translate a key into a label. */
        labelFor: (state) => (key) => {
            const path = `filterLabels.${key}`
            const translation = i18n.global.t(path)

            if (translation === path) {
                const fromCats = state.filterCategories.find(c => c.key === key)?.description

                if (fromCats) return fromCats

                const fromCache = state.categoryDescriptions[key]

                if (typeof fromCache === 'string' && fromCache.trim().length) return fromCache

                const fromMeta = state.filterOptions[key]?.description

                if (typeof fromMeta === 'string' && fromMeta.trim().length) return fromMeta

                return key
            }
            return translation
        },

        labelsParsedFor() {
            return (key) => {
                const meta = this.filterOptions[key]
                const sel = this.selected[key]
                if (sel == null) return true
                if (!meta) return false

                if (meta.type === 'BOOLEAN' || meta.type === 'NUMBER' || meta.type === 'PRICE') return true

                if (meta.type === 'OPTION' || meta.type === 'TPG_OPTION') {
                    if (!Array.isArray(sel) || sel.length === 0) return true
                    const all = (this.allOptionValues?.[key] || meta.optionValues || [])
                    if (!all.length) return false
                    const allowed = new Set(all.map(o => typeof o === 'string' ? String(o) : String(o.tpgCode ?? o.value ?? o.code ?? o.key ?? o.id ?? o.label ?? o.name ?? o.tpgName)))
                    return sel.every(v => allowed.has(String(v)))
                }

                return true
            }
        },

        selectedLabelsReady() {
            const keys = Object.keys(this.selected || {}).filter(k => {
                const v = this.selected[k]
                return v === true || (Array.isArray(v) ? v.length > 0 : v != null)
            })
            return keys.every(k => this.labelsParsedFor(k))
        },

        /** Get available option objects for OPTION/TPG_OPTION types. */
        optionsFor: (state) => (key) => state.filterOptions[key]?.optionValues || [],

        /** Pills for UI. */
        selectedFilters: (state) => {
            const keys = Array.from(new Set([
                ...state.filterCategories.map(c => c.key),
                ...Object.keys(state.selected || {})
            ]))

            const decimalLocale = getDecimalLocale()

            function isNum(v) {
                return typeof v === 'number' && Number.isFinite(v)
            }

            function decimalsOf(n) {
                const s = String(n)
                const i = s.indexOf('.')
                return i === -1 ? 0 : (s.length - i - 1)
            }

            return keys.flatMap(key => {
                const meta = state.filterOptions[key] || null
                const sel = state.selected[key]
                if (sel == null) return []

                const desc = (state.labelFor && state.labelFor(key)) || key

                switch (meta?.type || 'UNKNOWN') {
                    case 'TPG_OPTION':
                    case 'OPTION': {
                        if (!Array.isArray(sel) || sel.length === 0) return []
                        return sel.map(val => {
                            const o = meta?.optionValues?.find(o => (o?.tpgCode ?? o) === val)
                            const name = o?.tpgName ?? String(val)
                            return {key, desc, label: name, value: val}
                        })
                    }

                    case 'BOOLEAN': {
                        if (sel === true || (Array.isArray(sel) && sel.length > 0)) {
                            return [{key, desc, label: i18n.global.t('filterLabels.yes') || 'Yes', value: null}]
                        }
                        return []
                    }

                    case 'PRICE': {
                        if (!Array.isArray(sel) || sel.length !== 2) return []
                        const [a, b] = sel
                        if (!isNum(a) || !isNum(b)) return []
                        const userSet = state.userTouchedRange?.[key] === true
                        const equalsBounds = meta && isNum(meta.min) && isNum(meta.max) && a === meta.min && b === meta.max
                        if (!userSet && equalsBounds) return []
                        return [{
                            key,
                            desc,
                            label: `${parseNumberToLocalizedPrice(a)} – ${parseNumberToLocalizedPrice(b)}`,
                            value: null
                        }]
                    }

                    case 'NUMBER': {
                        if (!Array.isArray(sel) || sel.length !== 2) return []
                        const [a, b] = sel
                        if (!isNum(a) || !isNum(b)) return []
                        const userSet = state.userTouchedRange?.[key] === true
                        const equalsBounds = meta && isNum(meta.min) && isNum(meta.max) && a === meta.min && b === meta.max
                        if (!userSet && equalsBounds) return []
                        const decimals = Number.isInteger(meta?.__decimals)
                            ? meta.__decimals
                            : Math.max(decimalsOf(meta?.min ?? a), decimalsOf(meta?.max ?? b), decimalsOf(a), decimalsOf(b))
                        const aStr = formatLocalizedDecimal(a, {locale: decimalLocale, decimals})
                        const bStr = formatLocalizedDecimal(b, {locale: decimalLocale, decimals})
                        const unit = meta?.unitModifier ? `${meta.unitModifier}${meta.unit}` : meta?.unit || ''
                        return [{key, desc, label: `${aStr}${unit} – ${bStr}${unit}`, value: null}]
                    }

                    default: {
                        if (Array.isArray(sel) && sel.length === 2) {
                            const [a, b] = sel
                            return [{key, desc, label: `${a} – ${b}`, value: null}]
                        }
                        if (Array.isArray(sel)) {
                            return sel.map(v => ({key, desc, label: String(v), value: v}))
                        }
                        if (sel === true) {
                            return [{key, desc, label: i18n.global.t('filterLabels.yes') || 'Yes', value: null}]
                        }
                        return []
                    }
                }
            })
        },

        availableOptionsFor: (state) => (key) => {
            const meta = state.filterOptions[key]

            if (!meta) return []

            const avail = state.availability[key]
            const all = meta.optionValues || []

            if (!avail || !avail.values) return all

            const set = avail.values
            return all.filter(o => {
                const v = typeof o === 'string' ? o : (o.tpgCode ?? o.value ?? o.code ?? o.key ?? o.id ?? o.label ?? o.name ?? o.tpgName)
                return v != null && set.has(String(v))
            })
        },

        /** Merge meta with availability for rendering-only trimming. */
        effectiveMetaFor() {
            return (key) => {
                const meta = this.filterOptions[key]

                if (!meta) return null

                const avail = this.availability[key]

                if (!avail) return meta

                if (meta.type === 'BOOLEAN' || meta.type === 'NUMBER' || meta.type === 'PRICE') {
                    return meta
                }

                const set = avail.values

                if (!set) return meta

                const filtered = (meta.optionValues || []).filter(o => {
                    const v = typeof o === 'string' ? o : (o.tpgCode ?? o.value ?? o.code ?? o.key ?? o.id ?? o.label ?? o.name ?? o.tpgName)
                    return v != null && set.has(String(v))
                })
                return {...meta, optionValues: filtered}
            }
        }, selectedCount() {
            return this.selectedFilters.length
        }
    },

    actions: {
        setLastInteractedKey(key) {
            this.lastInteractedKey = key
        }, _onBaseTpgCodesChanged() {
            // Do not override the key while we are hydrating from the URL
            // or if a key has already been chosen (URL/session).
            if (this._isHydrating || this.lastInteractedKey) return;
            // After base tpg code changes always set focus to product category (last interacted)
            this.lastInteractedKey = 'productCategory'
            // eslint-disable-next-line no-empty
            try {
                sessionStorage.setItem('filters:lastOpenKey', 'productCategory')
            } catch {
                /**/
            }
        }, skipNextAvailabilityFor(key) {
            if (!key) return;
            this._skipNextAvailabilityFor.add(String(key));
        }, _clearSkipFor(key) {
            if (!key) return;
            this._skipNextAvailabilityFor.delete(String(key));
        }, setLoaded(payload) {
            this.loaded = payload
        }, markRangeTouched(key) {
            this.userTouchedRange[key] = true
        },

        /**
         * Initializes filter selections from the current URL.
         *
         * - Parses query parameters to infer filter values (boolean, range, or list).
         * - Updates `selected` accordingly without fetching filter metadata first.
         * - Silences watchers during setup to avoid premature API calls.
         * - Restores normal reactivity after initialization.
         * - Sets `lastInteractedKey` from sessionStorage or the last URL param.
         * - Finally triggers `applyFilters()` once to load initial products and filter data.
         */
        async initFiltersFromUrlOnce() {
            const store = useProductListStore()

            if (store.hideSideBar === '1' || store.tpgSideBarNavigation === '1') return

            this._prefilterOnlyAvailableAdded = false
            this._prefilterOnlyAvailableFallbackDone = false

            const url = new URL(window.location.href)
            const allParams = url.searchParams

            // gather non-ignored keys
            const candidates = []
            allParams.forEach((_, k) => {
                if (!shouldIgnoreParam(k)) candidates.push(k)
            })

            // The product list must not load before these filters were applied
            store.setInitialFilterPending(candidates.length > 0)

            // Learn the valid filter keys (and the unfiltered result) before judging the URL
            await this.fetchCategories()

            const toBaseKey = k => {
                const stripped = (k.endsWith('Min') || k.endsWith('Max')) ? k.slice(0, -3) : k

                return this.isKnownFilterKey(stripped) ? stripped : k
            }
            const keys = [...new Set(candidates.map(toBaseKey))].filter(k => this.isKnownFilterKey(k))
            const hadAnyUrlFilters = keys.length > 0

            // Prevent watchers from auto-applying during setup
            this.silenceSelectedMutations = (this.silenceSelectedMutations || 0) + 1
            this.beginHydration()

            try {
                // Only parse when there actually are params; otherwise skip parsing
                if (hadAnyUrlFilters) {
                    for (const key of keys) {
                        const values = allParams.getAll(key).filter(v => v != null && String(v).length)

                        // boolean-ish
                        if (values.length) {
                            const v = values[values.length - 1]

                            if (['1', 'true', 'yes', 'on'].includes(String(v).trim().toLowerCase())) {
                                this.selected[key] = true
                                continue
                            }
                        }

                        // range-ish
                        {
                            let minVal = null, maxVal = null
                            const namedMin = allParams.get(`${key}Min`)
                            const namedMax = allParams.get(`${key}Max`)

                            if (namedMin != null || namedMax != null) {
                                if (namedMin != null && namedMin.trim() !== '') minVal = Number(namedMin)
                                if (namedMax != null && namedMax.trim() !== '') maxVal = Number(namedMax)
                            }

                            if (minVal == null || maxVal == null) {
                                const r = parseRangeFromString(values[values.length - 1] || '')

                                if (r) [minVal, maxVal] = r
                            }

                            if (Number.isFinite(minVal) && Number.isFinite(maxVal)) {
                                this.selected[key] = [Number(minVal), Number(maxVal)]
                                this.userTouchedRange[key] = true
                                continue
                            }
                        }

                        // list-ish
                        {
                            const parts = values.flatMap(v => String(v).split(',').map(s => s.trim())).filter(Boolean)

                            if (parts.length) {
                                this.selected[key] = parts
                                continue
                            }
                        }
                    }
                }
            } finally {
                const prefilterAvailability = window.app?.product_list?.prefilterAvailability
                const wantsAvailabilityPrefilter = prefilterAvailability === '1'

                // if onlyAvailable is already in the URL and there are no other user filters,
                // treat it as a prefilter so the initial-load fallback is allowed to strip it.
                if (wantsAvailabilityPrefilter && allParams.has('onlyAvailable')) {
                    const raw = String(allParams.get('onlyAvailable') || '').trim().toLowerCase()
                    const onlyAvailTrue = ['1', 'true', 'yes', 'on'].includes(raw)

                    if (onlyAvailTrue) {
                        // "real" filters = not ignored keys and not onlyAvailable
                        const realKeys = []
                        allParams.forEach((_, k) => {
                            if (!shouldIgnoreParam(k) && k !== 'onlyAvailable') realKeys.push(k)
                        })

                        // If onlyAvailable is the only filter, we consider it prefilter-driven
                        if (realKeys.length === 0) {
                            this.selected.onlyAvailable = true
                            this._prefilterOnlyAvailableAdded = true
                            this._prefilterOnlyAvailableFallbackDone = false
                        }
                    }
                }

                if (wantsAvailabilityPrefilter && !allParams.has('onlyAvailable') && this.selected.onlyAvailable == null) {
                    this.selected.onlyAvailable = true
                    this._prefilterOnlyAvailableAdded = true
                    this._prefilterOnlyAvailableFallbackDone = false
                    url.searchParams.set('onlyAvailable', 'true')
                    window.history.replaceState({}, '', url)
                }
                this.endHydration()
                await nextTick()
                setTimeout(() => {
                    this.silenceSelectedMutations = Math.max(0, (this.silenceSelectedMutations || 0) - 1)
                }, 200)
            }

            // Decide lastInteractedKey strictly from session; do NOT derive from URL
            try {
                const stored = sessionStorage.getItem('filters:lastOpenKey');
                // If there's a stored key, use it; otherwise leave null (FilterBarContent will default to productCategory)
                this.lastInteractedKey = stored || null;
            } catch {
                this.lastInteractedKey = null;
            }

            this._firstLoadOptionsKey = this.lastInteractedKey || 'productCategory'

            // always run once so /v2/website/filter is called on first load
            try {
                await this.applyFilters({refreshOptions: true})
            } finally {
                store.setInitialFilterPending(false)
            }
        },
        /**
         * Is the current selection for `key` at its default (omittable)?
         *
         * Rules:
         * - BOOLEAN → default unless `true`.
         * - PRICE/NUMBER → default if [min,max] equals meta bounds,
         *   except when `userTouchedRange[key] === true` (keep even if equal).
         * - OPTION/TPG_OPTION → default if empty array.
         * - No meta yet → any actual selection is **not** default (prevents URL loss).
         *
         * Returns `true` to omit from URL/payload, `false` to keep.
         */
        isKnownFilterKey(key) {
            const known = new Set((this.filterCategories || []).map(c => c.key))

            // Without categories nothing is provable, so keep the key
            if (!known.size) return true

            return key === 'onlyAvailable' || known.has(key)
        },
        isSelectionDefault(key) {
            const meta = this.filterOptions[key]
            const sel = this.selected[key]

            // If there's no meta yet:
            // keep anything that is actually selected (so URL doesn't lose it)
            if (!meta) {
                if (sel === true) return false
                if (Array.isArray(sel)) return sel.length === 0
                return sel == null
            }

            if (meta.type === 'BOOLEAN') {
                return sel !== true
            }

            if (meta.type === 'PRICE' || meta.type === 'NUMBER') {
                if (!Array.isArray(sel) || sel.length !== 2) return true
                const [a, b] = sel
                const equalsBounds = Number.isFinite(meta.min) && Number.isFinite(meta.max) && a === meta.min && b === meta.max

                // If user (or URL) set the range, keep it in URL even if equals bounds
                if (this.userTouchedRange?.[key] === true) return false

                return equalsBounds
            }

            if (meta.type === 'TPG_OPTION' || meta.type === 'OPTION') {
                return !Array.isArray(sel) || sel.length === 0
            }

            return sel == null || (Array.isArray(sel) && sel.length === 0)
        },

        /** Build URL params from current restrictive selections. */
        buildFilterUrlParams() {
            const params = {}

            for (const key of Object.keys(this.selected)) {
                if (shouldIgnoreParam(key)) continue

                const meta = this.filterOptions[key]

                if (this.isSelectionDefault(key)) continue

                if (meta?.type === 'BOOLEAN') {
                    params[key] = 'true'
                    continue
                }

                if (meta?.type === 'PRICE' || meta?.type === 'NUMBER') {
                    const [a, b] = this.selected[key]
                    const cast = (n) => meta.type === 'NUMBER' ? String(parseInt(n, 10)) : String(Number(n))

                    params[key] = `${cast(a)},${cast(b)}`
                    continue
                }

                if (meta?.type === 'TPG_OPTION' || meta?.type === 'OPTION') {
                    const arr = (this.selected[key] || [])
                        .map((v) => String(v))
                        .filter(Boolean)

                    if (arr.length) {
                        params[key] = arr.join(',')
                    }
                    continue
                }

                // fallback (meta missing or unknown)
                const v = this.selected[key]

                if (Array.isArray(v)) {
                    if (v.length === 2 && v.every((n) => Number.isFinite(+n))) {
                        params[key] = `${v[0]},${v[1]}`
                    } else if (v.length) {
                        params[key] = v.map(String).join(',')
                    }
                } else if (v === true) {
                    params[key] = 'true'
                } else if (v != null) {
                    params[key] = String(v)
                }
            }

            return params
        },

        /** Debounced write of filters into URL. */
        writeFiltersToUrl() {
            if (!_urlSyncActive || !this._initDone || this._isHydrating) return
            if ((this.silenceSelectedMutations || 0) > 0) return

            const url = new URL(window.location.href)
            const sp = url.searchParams

            const visibleKeys = new Set(this.filterCategories.map(c => c.key))
            for (const key of visibleKeys) {
                sp.delete(key)
                sp.delete(`${key}Min`)
                sp.delete(`${key}Max`)
            }

            for (const key of Object.keys(this.selected)) {
                if (shouldIgnoreParam(key)) continue
                if (this.isSelectionDefault(key)) {
                    sp.delete(key)
                    sp.delete(`${key}Min`)
                    sp.delete(`${key}Max`)
                }
            }

            const params = this.buildFilterUrlParams()
            Object.entries(params).forEach(([k, v]) => sp.set(k, v))

            window.history.replaceState({}, '', url)
        },

        startUrlSync() {
            if (_urlSyncActive) return
            _urlSyncActive = true
            this.writeFiltersToUrl()
            this._onSelectedChanged = () => {
                if (_urlSyncTimer) clearTimeout(_urlSyncTimer)
                _urlSyncTimer = setTimeout(() => {
                    this.writeFiltersToUrl()
                }, 300)
            }
        }, stopUrlSync() {
            _urlSyncActive = false

            if (_urlSyncTimer) clearTimeout(_urlSyncTimer)
            _urlSyncTimer = null
            this._onSelectedChanged = null
        },

        /** Initialize fixed base TPG codes from product-list findAttributes. */
        /** Initialize fixed base TPG codes from product-list findAttributes. */
        initBaseTpgCodesFromFindAttributes(prod, {fromItemsFallback = true} = {}) {
            const fa = prod?.findAttributes || {}

            // 1) Prefer stable list context: classifications
            const classif = typeof fa.classifications === 'string' ? fa.classifications.trim() : ''
            if (classif) {
                const normalized = Array.from(new Set(classif.split(',').map(s => s.trim()).filter(Boolean))).sort()

                const key = normalized.join('|')
                if (key !== this._lastBaseTpgCodesKey) {
                    this.baseTpgCodes = normalized
                    this._lastBaseTpgCodesKey = key
                    this._baseTpgSource = 'classifications'
                    this._onBaseTpgCodesChanged() // focuses "productCategory"
                } else if (!this._baseTpgSource) {
                    this._baseTpgSource = 'classifications'
                }
                return
            }

            // 2) Optionally fallback to items (e.g., first load),
            //    but NOT during normal filter applies.
            if (!fromItemsFallback) return
            if (this._baseTpgSource === 'classifications') return // once we had a stable source, don't downgrade

            const next = (prod?.items || []).map(i => i.tpgCode).filter(Boolean)
            const normalized = Array.from(new Set(next)).sort()
            const key = normalized.join('|')

            if (key !== this._lastBaseTpgCodesKey) {
                this.baseTpgCodes = normalized
                this._lastBaseTpgCodesKey = key
                this._baseTpgSource = 'items'
                this._onBaseTpgCodesChanged() // focuses "productCategory"
            } else if (!this._baseTpgSource) {
                this._baseTpgSource = 'items'
            }
        },

        beginHydration() {
            this._isHydrating = true
        }, endHydration() {
            this._isHydrating = false
        },

        /**
         * Fetch categories once.
         */
        async fetchCategories() {
            if (this._fetchCatsPromise) return this._fetchCatsPromise
            if (this._initDone) return

            this._fetchCatsPromise = (async () => {
                this.loadingCategories = true
                this.error = null
                try {
                    const prod = useProductListStore()
                    this.initBaseTpgCodesFromFindAttributes(prod, {fromItemsFallback: true})

                    const {data} = await axios.post(`${this.apiEndpoint}/v2/website/filter`, {
                        website: this.website,
                        locale: this.locale,
                        tpgCodes: this.baseTpgCodes
                    })

                    const cats = Array.isArray(data.filterCategories) ? data.filterCategories : []
                    this.mergeCategoriesPreserveSelected ? this.mergeCategoriesPreserveSelected(cats) : (this.filterCategories = cats)

                    // focus productCategory if tpg base changed
                    const currentKey = (this.baseTpgCodes || []).join('|')
                    if (this._lastBaseTpgCodesKey !== currentKey) {
                        this._lastBaseTpgCodesKey = currentKey
                        // eslint-disable-next-line no-empty
                        // Do not override while hydrating from URL/session or if a key already exists
                        if (!this._isHydrating && !this.lastInteractedKey) {
                            try {
                                sessionStorage.setItem('filters:lastOpenKey', 'productCategory')
                            } catch { /* noop */
                            }

                            this.lastInteractedKey = 'productCategory'
                        }
                    }
                    cats.forEach(({key, description}) => {
                        if (key && typeof description === 'string' && description.trim()) {
                            this.categoryDescriptions = {...this.categoryDescriptions, [key]: description}
                        }
                    })

                    this.skus = data.skus || []
                    this.itemNos = data.itemNos || []
                    this.mergeCategoriesPreserveSelected(data.filterCategories || [])

                    this._initDone = true
                } catch (e) {
                    console.error(e)
                    this.error = e
                } finally {
                    this.loadingCategories = false
                }
            })()

            try {
                await this._fetchCatsPromise
            } finally {
                this._fetchCatsPromise = null
            }
        },

        /**
         * Fetch and (lightly) normalize filter metadata for a single category key.
         * - Builds request using current effective selections.
         * - Chooses tpgCodes from selected productCategory (if any) else baseTpgCodes.
         * - Seeds/repairs `selected[key]` so UI controls (slider/checkboxes) stay valid.
         * - Stores a stable "full list" for OPTION types; uses latest response only
         *   for availability trimming.
         */
        async fetchOptions(key) {
            const store = useProductListStore()

            if (store.hideSideBar === '1' || store.tpgSideBarNavigation === '1') return

            // Block all but the one allowed category on first load
            if (!this._hasAppliedOnce) {
                const allowed = this._firstLoadOptionsKey || 'productCategory'
                if (key !== allowed) {
                    // explicitly noop without toggling spinners forever
                    this.loadingOptions[key] = false
                    return
                }
            }

            // allow fetching even when there are no SKUs for these cases
            const allowWithoutSkus =
                key === 'productCategory' || key === 'salesPrice' || this.selected[key] != null
            if (!this.skus.length && !allowWithoutSkus) return

            this.loadingOptions[key] = true
            this.error = null

            // Prevent selection init/clamping from triggering applyFilters()
            this.silenceSelectedMutations = (this.silenceSelectedMutations || 0) + 1

            try {
                const hasPC = Array.isArray(this.selected.productCategory) && this.selected.productCategory.length > 0
                const pcKeys = hasPC ? this.resolveProductCategoryKeys() : []

                // All filters except productCategory itself
                const filterPayload = this.buildFilterPayload()
                const isProductCategory = key === 'productCategory'
                const isSalesPrice = key === 'salesPrice'
                let tpgCodesToSend
                let filterToSend

                if (isSalesPrice) {
                    // salesPrice: always global (page scope), no other filters
                    tpgCodesToSend = this.baseTpgCodes
                    filterToSend = []
                } else if (isProductCategory) {
                    // productCategory: ignore its own selection, but respect other filters
                    tpgCodesToSend = this.baseTpgCodes
                    filterToSend = filterPayload
                } else {
                    // all other filters: respect selected productCategory (narrow to its TPGs)
                    tpgCodesToSend = (hasPC && pcKeys.length ? pcKeys : this.baseTpgCodes)
                    filterToSend = filterPayload
                }

                const {data} = await axios.post(
                    `${this.apiEndpoint}/v2/website/filter/filterOptions/${encodeURIComponent(key)}`,
                    {
                        website: this.website,
                        locale: this.locale,
                        tpgCodes: Array.from(tpgCodesToSend),
                        filter: filterToSend.map(f => ({...f}))
                    }
                )

                this.everFetchedKeys.add(key)

                if (data.type === 'BOOLEAN') {
                    if (this.selected[key] == null) this.selected[key] = false
                    this.filterOptions = {...this.filterOptions, [key]: data}
                    return
                }

                if (data.type === 'PRICE' || data.type === 'NUMBER') {
                    if (!isNum(data.min) || !isNum(data.max)) return

                    // Track ever-seen extremes separately
                    const basePrev = this.allRangeBounds[key]
                    const baseNext = basePrev
                        ? {min: Math.min(basePrev.min, data.min), max: Math.max(basePrev.max, data.max)}
                        : {min: data.min, max: data.max}
                    if (!basePrev || baseNext.min !== basePrev.min || baseNext.max !== basePrev.max) {
                        this.allRangeBounds = {...this.allRangeBounds, [key]: baseNext}
                    }

                    // Availability is what server delivers
                    this.availability[key] = {type: data.type, min: data.min, max: data.max}

                    // write current availability into meta (what the UI reads)
                    // Keep extremes in __rangeAll for info/debug if needed
                    this.filterOptions = {
                        ...this.filterOptions,
                        [key]: {...data, min: data.min, max: data.max, __rangeAll: baseNext}
                    }

                    // Keep selection valid
                    const curSel = this.selected[key]
                    const touched = !!this.userTouchedRange[key]
                    if (!Array.isArray(curSel) || curSel.length !== 2) {
                        this.selected[key] = [data.min, data.max]
                        this.userTouchedRange[key] = false
                    } else if (touched) {
                        const nmin = Math.min(Math.max(curSel[0], data.min), data.max)
                        const nmax = Math.min(Math.max(curSel[1], data.min), data.max)
                        if (nmin !== curSel[0] || nmax !== curSel[1]) this.selected[key] = [nmin, nmax]
                    } else {
                        if (curSel[0] !== data.min || curSel[1] !== data.max) {
                            this.selected[key] = [data.min, data.max]
                        }
                    }
                    return
                }

                // OPTION / TPG_OPTION
                if (!Array.isArray(this.selected[key])) this.selected[key] = []
                if (!Array.isArray(this.allOptionValues[key]) || this.allOptionValues[key].length === 0) {
                    this.allOptionValues = {...this.allOptionValues, [key]: data.optionValues || []}
                }
                const cur = data.optionValues || []
                const availSet = new Set(
                    cur.map(o => typeof o === 'string' ? String(o) :
                        String(o.tpgCode ?? o.value ?? o.code ?? o.key ?? o.id ?? o.label ?? o.name ?? o.tpgName)
                    ).filter(Boolean)
                )
                this.availability[key] = {type: data.type, values: availSet}
                const full = this.allOptionValues[key]
                this.filterOptions = {...this.filterOptions, [key]: {...data, optionValues: full}}
            } catch (e) {
                console.error(e)
                this.error = e
            } finally {
                this.loadingOptions[key] = false
                this._hasAppliedOnce = true
                this._firstLoadOptionsKey = null
                await nextTick()
                setTimeout(() => {
                    this.silenceSelectedMutations = Math.max(0, (this.silenceSelectedMutations || 0) - 1)
                }, 200)
            }
        },

        /**
         * Build request-ready filters from current selections.
         * - Skips `productCategory`.
         * - BOOLEAN: include only when true.
         * - PRICE/NUMBER: include only when [min,max] differs from meta defaults.
         * - OPTION/TPG_OPTION: include only when array is non-empty.
         * - Fallback: send only meaningful values (true, valid range, non-empty list).
         */
        buildFilterPayload() {
            const payload = [];

            for (const key of Object.keys(this.selected)) {
                if (key === 'productCategory') continue; // never sent as filter entry

                const meta = this.filterOptions[key] || null;
                const sel = this.selected[key];

                const isList = Array.isArray(sel);
                const has2Nums = isList && sel.length === 2 && sel.every(n => Number.isFinite(+n));

                switch (meta?.type) {
                    case 'BOOLEAN': {
                        if (sel === true) payload.push({key, booleanValue: true});
                        break;
                    }

                    case 'PRICE':
                    case 'NUMBER': {
                        if (!has2Nums) break;
                        const [a, b] = sel.map(Number);
                        // do not send when the range equals the default bounds
                        const isDefault = Number.isFinite(meta?.min) && Number.isFinite(meta?.max) && a === meta.min && b === meta.max;
                        if (!isDefault) payload.push({key, min: a, max: b});
                        break;
                    }

                    case 'TPG_OPTION':
                    case 'OPTION': {
                        // only send non-empty arrays
                        if (isList && sel.length > 0) {
                            payload.push({key, optionValue: sel});
                        }
                        break;
                    }

                    default: {
                        // defensive fallbacks (send only "meaningful" selections)
                        if (sel === true) payload.push({key, booleanValue: true}); else if (has2Nums) {
                            const [a, b] = sel.map(Number);
                            payload.push({key, min: a, max: b});
                        } else if (isList && sel.length > 0) {
                            payload.push({key, optionValue: sel});
                        }
                    }
                }
            }

            return payload;
        },

        /** Returns selected productCategory send-values (TPG keys). */
        resolveProductCategoryKeys() {
            const sel = this.selected['productCategory']
            if (!Array.isArray(sel) || !sel.length) return []

            const meta = this.filterOptions['productCategory']
            if (!meta || !Array.isArray(meta.optionValues)) {
                return sel
            }

            const allowed = new Set(meta.optionValues.map(o => typeof o === 'string' ? o : (o.tpgCode ?? o.value ?? o.code)))
            return sel.filter(v => allowed.has(String(v)))
        },
        /**
         * Refresh ONLY the availability/meta for the given filter keys.
         *
         * - Posts current (effective) filters to `/filterOptions/{key}`.
         * - Special cases (unfiltered-by-others):
         *   • `productCategory` and `salesPrice` use **baseTpgCodes** and send **no filters** (full page scope).
         * - BOOLEAN → marks as possible.
         * - NUMBER/PRICE → updates availability range and, if the server widened bounds,
         *   syncs store meta and clamps/expands the current selection (without triggering
         *   applyFilters thanks to `silenceSelectedMutations`).
         * - OPTION/TPG_OPTION → stores currently available option values as a Set
         *   (used to gray/hide options while keeping the full list in `filterOptions`).
         */
        async refreshAvailability(keys) {
            if (!Array.isArray(keys) || !keys.length) return

            const filter = this.buildFilterPayload()
            const hasPC = Array.isArray(this.selected.productCategory) && this.selected.productCategory.length > 0
            const pcKeys = hasPC ? this.resolveProductCategoryKeys() : []
            const defaultTpg = hasPC && pcKeys.length ? pcKeys : this.baseTpgCodes

            await Promise.all(keys.map(async (key) => {
                try {
                    const isProductCategory = key === 'productCategory'
                    const isSalesPrice = key === 'salesPrice'
                    let tpgCodesToSend
                    let filterToSend

                    if (isSalesPrice) {
                        // salesPrice: global, no filters
                        tpgCodesToSend = this.baseTpgCodes
                        filterToSend = []
                    } else if (isProductCategory) {
                        // productCategory: ignore its own selection, respect other filters
                        tpgCodesToSend = this.baseTpgCodes
                        filterToSend = filter
                    } else {
                        // others: respect selected productCategory
                        tpgCodesToSend = defaultTpg
                        filterToSend = filter
                    }

                    const {data} = await axios.post(
                        `${this.apiEndpoint}/v2/website/filter/filterOptions/${encodeURIComponent(key)}`,
                        {
                            website: this.website,
                            locale: this.locale,
                            tpgCodes: Array.from(tpgCodesToSend),
                            filter: filterToSend.map(f => ({...f}))
                        }
                    )

                    if (data.type === 'BOOLEAN') {
                        this.availability[key] = {type: 'BOOLEAN', possible: true}
                        return
                    }

                    if (data.type === 'NUMBER' || data.type === 'PRICE') {
                        this.availability[key] = {type: data.type, min: data.min, max: data.max}

                        // maintain ever-seen extremes separately
                        const basePrev = this.allRangeBounds[key] || null
                        const baseNext = basePrev
                            ? {min: Math.min(basePrev.min, data.min), max: Math.max(basePrev.max, data.max)}
                            : {min: data.min, max: data.max}
                        if (!basePrev || baseNext.min !== basePrev.min || baseNext.max !== basePrev.max) {
                            this.allRangeBounds = {...this.allRangeBounds, [key]: baseNext}
                        }

                        // write current availability into meta so components update**
                        if (this.filterOptions[key]) {
                            this.filterOptions = {
                                ...this.filterOptions,
                                [key]: {...this.filterOptions[key], min: data.min, max: data.max, __rangeAll: baseNext}
                            }
                        } else {
                            // In case meta wasn't set yet
                            this.filterOptions = {
                                ...this.filterOptions,
                                [key]: {...data, min: data.min, max: data.max, __rangeAll: baseNext}
                            }
                        }

                        // keep selection valid
                        const curSel = this.selected[key]
                        const touched = !!this.userTouchedRange[key]
                        if (Array.isArray(curSel) && curSel.length === 2) {
                            let nextSel
                            if (!touched) {
                                nextSel = [data.min, data.max]
                            } else {
                                nextSel = [
                                    Math.min(Math.max(curSel[0], data.min), data.max),
                                    Math.min(Math.max(curSel[1], data.min), data.max)
                                ]
                            }
                            if (nextSel[0] !== curSel[0] || nextSel[1] !== curSel[1]) {
                                this.silenceSelectedMutations = (this.silenceSelectedMutations || 0) + 1
                                this.selected[key] = nextSel
                                await nextTick()
                                setTimeout(() => {
                                    this.silenceSelectedMutations = Math.max(0, (this.silenceSelectedMutations || 0) - 1)
                                }, 200)
                            }
                        } else if (curSel == null) {
                            this.silenceSelectedMutations = (this.silenceSelectedMutations || 0) + 1
                            this.selected[key] = [data.min, data.max]
                            await nextTick()
                            setTimeout(() => {
                                this.silenceSelectedMutations = Math.max(0, (this.silenceSelectedMutations || 0) - 1)
                            }, 200)
                        }
                        return
                    }

                    // OPTION / TPG_OPTION
                    const set = new Set(
                        (data.optionValues || [])
                            .map(o => typeof o === 'string'
                                ? String(o)
                                : String(o.tpgCode ?? o.value ?? o.code ?? o.key ?? o.id ?? o.label ?? o.name ?? o.tpgName)
                            ).filter(Boolean)
                    )
                    this.availability[key] = {type: data.type, values: set}
                } catch (e) {
                    console.error('[refreshAvailability] failed for', key, e)
                }
            }))
        },

        /**
         * Apply current selections:
         * - Posts effective filters to /v2/website/filter (aborting any prior call).
         * - Updates skus/itemNos and pushes them into the productList store (triggers list GET).
         * - Refreshes availability only after the first apply, and only for relevant keys,
         *   respecting the skip-once guard.
         */
        async applyFilters({refreshOptions = true} = {}) {
            const store = useProductListStore()

            if (store.hideSideBar === '1' || store.tpgSideBarNavigation === '1') return

            if (this._isHydrating || this.loadingApply) return;

            const prod = useProductListStore();
            this.initBaseTpgCodesFromFindAttributes(prod, {fromItemsFallback: false});

            const filter = this.buildFilterPayload();
            const hasPC = Array.isArray(this.selected.productCategory) && this.selected.productCategory.length > 0;
            const categoryKeys = hasPC ? this.resolveProductCategoryKeys() : [];
            const tpgCodesToSend = hasPC && categoryKeys.length ? categoryKeys : this.baseTpgCodes;

            // Abort any in-flight request
            if (this._abortCtrl) this._abortCtrl.abort();
            this._abortCtrl = new AbortController();

            this.loadingApply = true;
            this.error = null;

            try {
                let data

                // Nothing selected yet, so fetchCategories already holds the unfiltered result
                const nothingSelected = !hasPC && (!Array.isArray(filter) || filter.length === 0)

                if (!this._hasAppliedOnce && this._initDone && nothingSelected) {
                    data = {itemNos: this.itemNos, skus: this.skus, filterCategories: this.filterCategories}
                } else {
                    ({data} = await axios.post(
                        `${this.apiEndpoint}/v2/website/filter`,
                        {website: this.website, locale: this.locale, tpgCodes: tpgCodesToSend, filter},
                        {signal: this._abortCtrl.signal}
                    ))
                }

                // minimal prefilter fallback (initial load only)
                const wantsAvailabilityPrefilter = window.app?.product_list?.prefilterAvailability === '1'
                const isInitialLoad = !this._hasAppliedOnce

                const itemNos0 = Array.isArray(data?.itemNos) ? data.itemNos : []
                const skus0 = Array.isArray(data?.skus) ? data.skus : []
                const isEmpty0 = itemNos0.length === 0 && skus0.length === 0

                if (
                    wantsAvailabilityPrefilter &&
                    isInitialLoad &&
                    this._prefilterOnlyAvailableAdded === true &&
                    this._prefilterOnlyAvailableFallbackDone === false &&
                    isEmpty0
                ) {
                    this._prefilterOnlyAvailableFallbackDone = true

                    // remove the auto-added prefilter from selected + URL
                    this.silenceSelectedMutations = (this.silenceSelectedMutations || 0) + 1
                    try {
                        delete this.selected.onlyAvailable
                        const u = new URL(window.location.href)
                        u.searchParams.delete('onlyAvailable')
                        window.history.replaceState({}, '', u)
                    } finally {
                        await nextTick()
                        setTimeout(() => {
                            this.silenceSelectedMutations = Math.max(0, (this.silenceSelectedMutations || 0) - 1)
                        }, 0)
                    }

                    // rebuild payload without onlyAvailable and retry /filter once
                    const filter2 = this.buildFilterPayload()
                    ;({data} = await axios.post(
                        `${this.apiEndpoint}/v2/website/filter`,
                        {website: this.website, locale: this.locale, tpgCodes: tpgCodesToSend, filter: filter2},
                        {signal: this._abortCtrl.signal}
                    ))

                    // since onlyAvailable was auto-added, mark as no longer active
                    this._prefilterOnlyAvailableAdded = false
                }

                this.skus = data.skus || [];
                this.itemNos = data.itemNos || [];

                if (Array.isArray(data.filterCategories)) {
                    this.mergeCategoriesPreserveSelected(data.filterCategories);
                    if (!this._initDone) this._initDone = true;
                }

                // Push itemNos into product list store and reset page
                const productStore = useProductListStore();
                productStore.findAttributes.itemNumbers = (this.itemNos || []).join(',');
                delete productStore.findAttributes.classifications;

                productStore.setInitialFilterPending(false);
                if (this._hasAppliedOnce) {
                    productStore.setPageNumber(0);
                }

                if (this.itemNos && this.itemNos.length > 0) {
                    // When results fetch list and sylius info
                    if (productStore.isEmptyFilterResult) productStore.setIsEmptyFilterResult(false);
                    await productStore.get();
                    //productStore.handleSyliusCall();
                } else {
                    // When no results deterministic empty state
                    productStore.setItems([]);
                    productStore.setIsEmpty(true);
                    productStore.setNumberOfTotalItems(0);
                    productStore.setTotalPages(0);
                    productStore.setPageNumber(0);
                    productStore.setLoaded(true);
                    productStore.setSyliusLoaded(true);
                    productStore.setIsEmptyFilterResult(true);

                    // fetch options for exactly one category so pills/labels can render
                    const ensureKey = this.lastInteractedKey || (this.filterCategories.some(c => c.key === 'productCategory') ? 'productCategory' : this.filterCategories[0]?.key);
                    if (ensureKey) await this.fetchOptions(ensureKey);
                }

                const isFirstApply = !this._hasAppliedOnce;

                if (!isFirstApply) {
                    const keysToRefresh = new Set();

                    // refresh only last interacted key
                    const lk = this.lastInteractedKey;

                    if (
                        lk &&
                        lk !== 'productCategory' &&
                        this.filterCategories.some(c => c.key === lk) &&
                        !this.loadingOptions?.[lk] &&
                        !this._skipNextAvailabilityFor.has(lk)
                    ) {
                        keysToRefresh.add(lk);
                    }

                    if (keysToRefresh.size > 0) {
                        await this.refreshAvailability([...keysToRefresh]);
                    }
                } else {
                    // First page load skip any availability refresh to avoid URL-param fetches
                }

                // clear one-shot guards
                this._clearSkipFor(this.lastInteractedKey);
                this._clearSkipFor('productCategory');

                this._hasAppliedOnce = true;
            } catch (e) {
                if (e.name !== 'CanceledError' && e.code !== 'ERR_CANCELED') {
                    console.error(e);
                    this.error = e;
                }
            } finally {
                if (refreshOptions) {
                    this.silenceSelectedMutations = Math.max(0, this.silenceSelectedMutations - 1);
                }
                this.loadingApply = false;
                this.setLoaded(true);
            }
        },

        /**
         * Reset all filters to defaults and repopulate products.
         * - Clears URL params and `selected` (ranges -> [min,max] so sliders stay visible).
         * - Suppresses URL sync during the reset to avoid noisy writes.
         * - Calls `applyFilters()` with a clean payload.
         * - Optionally re-fetches meta/options for one category (`refetchKey`) to refresh its list.
         *
         * @param {Object} [opts]
         * @param {string|null} [opts.refetchKey]  Category key to re-fetch after reset (falls back to last-open key).
         * @returns {Promise<void>}
         */
        async clearSelected({refetchKey} = {}) {
            // Immediately show skeletons in both the filter bar and the product list
            this.setLoaded(false)
            // eslint-disable-next-line no-empty
            try {
                useProductListStore().setLoaded(false)
            } catch {
                /**/
            }

            this._isResetting = true
            // After reset prefer productCategory
            refetchKey = 'productCategory'
            // eslint-disable-next-line no-empty
            try {
                sessionStorage.setItem('filters:lastOpenKey', 'productCategory')
            } catch {
                /**/
            }

            // Pause reactive URL sync while we mutate
            this.silenceSelectedMutations = (this.silenceSelectedMutations || 0) + 1;

            try {
                // Reset visible keys to their *default* selections so the UI (sliders) stay rendered
                Object.keys(this.filterOptions).forEach((key) => {
                    if (shouldIgnoreParam(key)) return;
                    const meta = this.filterOptions[key];
                    if (!meta) {
                        delete this.selected[key];
                        return;
                    }

                    if (meta.type === 'BOOLEAN') {
                        this.selected[key] = false;
                    } else if (meta.type === 'PRICE' || meta.type === 'NUMBER') {
                        this.selected[key] = [meta.min, meta.max]; // keep slider visible
                        this.userTouchedRange[key] = false;
                    } else if (meta.type === 'TPG_OPTION' || meta.type === 'OPTION') {
                        this.selected[key] = [];
                    } else {
                        delete this.selected[key];
                    }
                });

                // Remove any stray selections for unknown keys
                Object.keys(this.selected).forEach((key) => {
                    if (!this.filterOptions[key]) delete this.selected[key];
                });

                // Clear URL params for all known categories
                const url = new URL(window.location.href);
                this.filterCategories.forEach(({key}) => {
                    url.searchParams.delete(key);
                    url.searchParams.delete(`${key}Min`);
                    url.searchParams.delete(`${key}Max`);
                });
                window.history.replaceState({}, '', url);

                // no auto-refresh on last interacted during reset
                this.lastInteractedKey = null
            } finally {
                // Resume URL sync on next tick
                setTimeout(() => {
                    this.silenceSelectedMutations = Math.max(0, (this.silenceSelectedMutations || 0) - 1);
                }, 0);
            }

            // Re-apply products + categories with a clean payload
            // (defaults are skipped by buildFilterPayload)
            await this.applyFilters({refreshOptions: true});

            // Now re-fetch options for the currently open category (fresh list/availability)
            if (refetchKey && this.filterCategories.some(c => c.key === refetchKey)) {
                // Avoid duplicate concurrent fetches
                if (!this.loadingOptions?.[refetchKey]) {
                    await this.fetchOptions(refetchKey);
                    // mark as fetched so subsequent availability refreshes work as usual
                    this.everFetchedKeys.add(refetchKey);
                }
            }

            // sets focus after processing everything
            this.setLastInteractedKey('productCategory')
            // eslint-disable-next-line no-empty
            try {
                sessionStorage.setItem('filters:lastOpenKey', 'productCategory')
            } catch {
                /**/
            }
            this._isResetting = false
        },

        /** Reset one category (all types). */
        resetCategory(key) {
            const meta = this.filterOptions[key]
            if (!meta) {
                delete this.selected[key]
                this.forceRemoveParamFromUrl(key)
                return
            }

            const sel = this.selected[key]

            if (meta.type === 'BOOLEAN' || meta.type === 'PRICE' || meta.type === 'NUMBER') {
                this.removeFilter(key, null)
                return
            }

            if (Array.isArray(sel) && sel.length) {
                sel.slice().forEach(val => this.removeFilter(key, val))
            }
        },

        forceRemoveParamFromUrl(key) {
            const url = new URL(window.location.href)
            url.searchParams.delete(key)
            url.searchParams.delete(`${key}Min`)
            url.searchParams.delete(`${key}Max`)
            window.history.replaceState({}, '', url)
        },

        /**
         * Remove a single selected filter and immediately sync the URL.
         */
        async removeFilter(key, value) {
            const meta = this.filterOptions[key]

            // Immediately show skeletons in both the filter bar and the product list
            this.setLoaded(false)
            try {
                useProductListStore().setLoaded(false)
                // eslint-disable-next-line no-empty
            } catch {
            }

            if (!meta) {
                delete this.selected[key]
                this.forceRemoveParamFromUrl(key)
            } else if (meta.type === 'BOOLEAN') {
                this.selected[key] = false
            } else if (meta.type === 'PRICE' || meta.type === 'NUMBER') {
                this.selected[key] = [meta.min, meta.max]
                this.userTouchedRange[key] = false
            } else if (meta.type === 'TPG_OPTION' || meta.type === 'OPTION') {
                this.selected[key] = (this.selected[key] || []).filter(v => String(v) !== String(value))
                // If this empties the array, normalize to [] so URL sync knows to drop it
                if (!this.selected[key].length) this.selected[key] = []
            } else {
                delete this.selected[key]
            }

            // Keep URL in sync
            const url = new URL(window.location.href)
            if (this.isSelectionDefault(key)) {
                url.searchParams.delete(key)
                url.searchParams.delete(`${key}Min`)
                url.searchParams.delete(`${key}Max`)
            } else {
                if (meta?.type === 'PRICE' || meta?.type === 'NUMBER') {
                    const [a, b] = this.selected[key]
                    url.searchParams.set(key, `${a},${b}`)
                } else if (meta?.type === 'BOOLEAN') {
                    url.searchParams.set(key, 'true')
                } else if (meta?.type === 'TPG_OPTION' || meta?.type === 'OPTION') {
                    const arr = (this.selected[key] || []).map(String).filter(Boolean)
                    if (arr.length) url.searchParams.set(key, arr.join(','))
                    else {
                        url.searchParams.delete(key)
                        url.searchParams.delete(`${key}Min`)
                        url.searchParams.delete(`${key}Max`)
                    }
                }
            }
            window.history.replaceState({}, '', url)

            // Debounced URL writer
            this._onSelectedChanged && this._onSelectedChanged()

            // Make availability focus the category we touched
            this.setLastInteractedKey(key)

            // Re-run the full pipeline so itemNos + products refresh,
            // even when initial load had 0 results.
            await this.applyFilters({refreshOptions: true})
        },

        /** Change one end of a numeric range. */
        updateRange(key, index, raw) {
            const meta = this.filterOptions[key]
            if (!meta) return

            const parsed = meta.type === 'NUMBER' ? parseInt(raw, 10) : parseFloat(raw)

            const clamped = Math.min(Math.max(parsed, meta.min), meta.max)
            const arr = [...(this.selected[key] || [meta.min, meta.max])]
            arr[index] = clamped
            this.selected[key] = arr
            this.userTouchedRange[key] = true
        },

        /**
         * Replace the visible categories with a new list from the backend **but**
         * keep any existing selections/meta for categories that disappeared.
         */
        mergeCategoriesPreserveSelected(newCats = []) {
            const incoming = Array.isArray(newCats) ? newCats : []
            this.filterCategories = incoming
            incoming.forEach(({key, description}) => {
                if (this.selected[key] === undefined) this.selected[key] = null
                if (key && typeof description === 'string' && description.trim().length) {
                    this.categoryDescriptions = {...this.categoryDescriptions, [key]: description}
                }
            })

        },
    }
})
